import { useTranslation } from 'next-i18next'

import { LOGIN_VIEW } from '~/auth/types/login.types'
import { slurEmail } from '~/auth/utils/auth.utils'
import { useTitle } from '~/utils/tags'
import Button from '~/components/Buttons/Button'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectFields, selectSimpleUserProfile, setViewActionCreator } from '~/auth/slices/login.slice'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

import { AuthTypes } from '../types/auth.types'

import { RecoverPasswordButton } from './RecoverPasswordButton'

import ButtonFacebookLogin from './ButtonFacebookLogin'
import ButtonGoogleLogin from './ButtonGoogleLogin'

const AvatarPlaceholder = 'avatar-portraits/hom-default-avatar.png'

interface Props {
  provider: AuthTypes
}
const mediaQuery = [{ screen: '(min-width: 280px)', width: '160px' }]

export default function AuthenticateWithSSO({ provider }: Props): React.ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation(['logIn', 'authenticate'])
  useTitle(t('logIn:title'))

  const fields = useAppSelector(selectFields)
  const simpleUserProfile = useAppSelector(selectSimpleUserProfile)

  const showPasswordLoginForm = (): void => {
    dispatch(setViewActionCreator(LOGIN_VIEW.ENTER_PASSWORD))
  }

  const showDefaultLoginView = (): void => {
    dispatch(setViewActionCreator(LOGIN_VIEW.DEFAULT))
  }

  return (
    <div className="w-86 lg:w-125 h-full space-y-4 flex flex-col items-center justify-center text-center">
      <div className="space-y-2 font-medium">
        <div className="text-lg lg:text-h3">{t('authenticate:logIn')}</div>
        <div>{t('authenticate:welcomeBack', { user: simpleUserProfile.nickname })}</div>
      </div>
      <ResponsiveImage
        alt="avatar"
        height={160}
        imageClassName="w-40 h-40 rounded-full"
        imageWidths="160, 200"
        mediaQuery={mediaQuery}
        srcKey={simpleUserProfile.avatar || AvatarPlaceholder}
        width={160}
      />
      <span>{slurEmail(fields.email)}</span>
      {provider === AuthTypes.GOOGLE ? (
        <ButtonGoogleLogin buttonText={t('authenticate:continueWithGoogle')} redirectBack />
      ) : (
        <ButtonFacebookLogin buttonText={t('authenticate:continueWithFacebook')} redirectBack />
      )}
      {simpleUserProfile.userHasPassword && (
        <div className="w-full space-y-4">
          <Button
            className="bg-transparent border border-solid border-black-light hover:bg-white-offWhite"
            isFullWidth
            onClick={showPasswordLoginForm}
            size="small"
            type="reset"
          >
            <div className="text-sm font-semibold"> {t('authenticate:continueWithPassword')}</div>
          </Button>
        </div>
      )}
      <span>{t('authenticate:notYou')}</span>{' '}
      <button className="font-medium hover:underline" onClick={showDefaultLoginView}>
        {t('authenticate:useDifferentAccount')}
      </button>
      <RecoverPasswordButton />
    </div>
  )
}
